// One stroke-detection (rowing detector) instance for the whole app, mounted
// above the routes so it keeps running — with its own GPS feed — on every tab.
import { createContext, useContext, useEffect, useRef } from 'react';
import { useWorkout } from '@/contexts/WorkoutContext';
import { useStrokeDetection } from '@/hooks/useStrokeDetection';

const StrokeDetectionContext = createContext(null);

export function StrokeDetectionProvider({ children }) {
  const { workoutActive, workoutPaused, pm5Device, crewzConnected, setStrokes, setStrokeRate } = useWorkout();
  const sd = useStrokeDetection({ crewzConnected, pm5Connected: !!pm5Device, paused: workoutPaused });

  // Start once per session (not on resume, tab change or a denied permission), stop at session end / PM5 connect.
  const startedRef = useRef(false);
  useEffect(() => {
    if (!workoutActive) startedRef.current = false;
    if (workoutActive && !workoutPaused && !pm5Device && !sd.isTracking && !startedRef.current) {
      startedRef.current = true;
      sd.startTracking();
    }
    if ((!workoutActive || pm5Device) && sd.isTracking) sd.stopTracking();
    if (pm5Device) startedRef.current = false;
  }, [workoutActive, workoutPaused, pm5Device, sd]);

  // Feed rate and ST (session total strokes) into the workout state.
  useEffect(() => {
    if (pm5Device || !sd.isTracking) return;
    if (sd.strokeRate > 0) setStrokeRate(sd.strokeRate);
    setStrokes(sd.totalStrokes);
  }, [sd.strokeRate, sd.totalStrokes, sd.isTracking, pm5Device, setStrokes, setStrokeRate]);

  return <StrokeDetectionContext.Provider value={sd}>{children}</StrokeDetectionContext.Provider>;
}

export const useSharedStrokeDetection = () => useContext(StrokeDetectionContext);
